const GUID =
	/^[{(]?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}[)}]?$/i;
const GUID_COMPACT = /^[0-9a-f]{32}$/i;

function guidFromString(raw: string): string | null {
	const trimmed = raw.trim().replace(/^[{(]|[)}]$/g, "");
	if (GUID.test(raw.trim())) return trimmed;
	if (!GUID_COMPACT.test(trimmed)) return null;
	return `${trimmed.slice(0, 8)}-${trimmed.slice(8, 12)}-${trimmed.slice(12, 16)}-${trimmed.slice(16, 20)}-${trimmed.slice(20)}`;
}

function fileIdFromRecord(value: object): string | null {
	const record = value as Record<string, unknown>;
	for (const key of ["fileInfoId", "FileInfoId", "id", "Id"] as const) {
		const id = storedFileId(record[key]);
		if (id) return id;
	}
	return null;
}

/**
 * File id for a signature value: a guid string, or `id` / `fileInfoId` on a file record.
 */
export function storedFileId(value: unknown): string | null {
	if (typeof value === "string") {
		const direct = guidFromString(value);
		if (direct) return direct;
		const trimmed = value.trim();
		if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
		try {
			return storedFileId(JSON.parse(trimmed) as unknown);
		} catch {
			return null;
		}
	}
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return fileIdFromRecord(value);
}

export interface StoredFileDetails {
	id: string;
	name: string | null;
	size: number | null;
	type: string | null;
}

function stringProp(
	record: Record<string, unknown>,
	keys: readonly string[],
): string | null {
	for (const key of keys) {
		const value = record[key];
		if (typeof value === "string" && value.trim().length > 0) {
			return value.trim();
		}
	}
	return null;
}

function sizeProp(record: Record<string, unknown>): number | null {
	for (const key of ["size", "Size"] as const) {
		const value = record[key];
		if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
			return value;
		}
		if (typeof value === "string" && value.trim() !== "") {
			const parsed = Number(value);
			if (Number.isFinite(parsed) && parsed >= 0) return parsed;
		}
	}
	return null;
}

/**
 * Id plus name, size, and type when the stored value is a file record.
 * A bare file id has no name or size.
 */
export function storedFileDetails(value: unknown): StoredFileDetails | null {
	const id = storedFileId(value);
	if (!id) return null;
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return { id, name: null, size: null, type: null };
	}
	const record = value as Record<string, unknown>;
	return {
		id,
		name: stringProp(record, ["name", "Name"]),
		size: sizeProp(record),
		type: stringProp(record, ["type", "Type", "contentType", "ContentType"]),
	};
}

export interface StoredFileSession {
	name: string;
	size: number;
	type: string;
}

export interface StoredFileRemote {
	fileName: string | null;
	size: number;
}

function fileTypeForIcon(type: string | null, name: string): string {
	if (type && type !== "empty") return type;
	const dot = name.lastIndexOf(".");
	if (dot > 0 && dot < name.length - 1) {
		const ext = name.slice(dot + 1).toLowerCase();
		if (ext.length > 0 && ext.length <= 8 && !ext.includes("/")) return ext;
	}
	return "empty";
}

/**
 * Name and size shown for a file field. A file chosen in this session wins,
 * then the loaded file record, then a downloaded copy.
 */
export function resolveStoredFilePresentation(
	value: unknown,
	session: StoredFileSession | null,
	remote: StoredFileRemote | null,
): { id: string; name: string; size: number; type: string } | null {
	const details = storedFileDetails(value);
	if (!details) return null;
	const name = session?.name ?? details.name ?? remote?.fileName ?? details.id;
	const size = session?.size ?? details.size ?? remote?.size ?? 0;
	return {
		id: details.id,
		name,
		size,
		type: fileTypeForIcon(session?.type ?? details.type, name),
	};
}
